import { SimpleSelect } from '@retailer-platform/shared-components'
import React, { type FunctionComponent, useEffect, useMemo, useState } from 'react'
import { type FormContextValues } from 'react-hook-form'
import { useDomainMessages } from 'domains/collections/utils/domain/intl'
import { type CollectionsFormData } from 'domains/collections/components/collections-form/utils/collectionsForm.types'
import { CollectionsFormInputWrapper } from 'domains/collections/components/collections-form/components/form-input/CollectionsFormInputWrapper'
import { CollectionStatusEnum } from './utils/CollectionDetails.types'

interface CollectionsDetailsStatusProps {
  register: FormContextValues<CollectionsFormData>['register']
  getValues: FormContextValues<CollectionsFormData>['getValues']
  setValue: FormContextValues<CollectionsFormData>['setValue']
  retailerId: string
}

type StatusSelectOption = {
  label: string
  value: CollectionStatusEnum
  isDisabled?: boolean
}

export const CollectionsDetailsStatus: FunctionComponent<CollectionsDetailsStatusProps> = ({
  register,
  getValues,
  setValue,
  retailerId,
}) => {
  const messages = useDomainMessages({
    label: 'collectionsDomain.collectionsForm.section.details.status.label',
    placeholder: 'collectionsDomain.collectionsForm.section.details.status.label',
    active: 'collectionsDomain.collectionsForm.section.details.status.active',
    activeAdminOnly: 'collectionsDomain.collectionsForm.section.details.status.activeAdminOnly',
    inactive: 'collectionsDomain.collectionsForm.section.details.status.inactive',
  })

  const [selectedOption, setSelectedOption] = useState(
    getValues('details.active')
      ? CollectionStatusEnum.active
      : getValues('details.activeAdminOnly')
      ? CollectionStatusEnum.activeAdminOnly
      : CollectionStatusEnum.inactive
  )

  const handleOnChange = (selectedOption: CollectionStatusEnum) => {
    setSelectedOption(selectedOption)
    if (selectedOption === CollectionStatusEnum.active) {
      setValue('details.active', true)
      setValue('details.activeAdminOnly', false)
    } else if (selectedOption === CollectionStatusEnum.activeAdminOnly) {
      setValue('details.active', false)
      setValue('details.activeAdminOnly', true)
    } else {
      setValue('details.active', false)
      setValue('details.activeAdminOnly', false)
    }
  }

  const statusOptions: StatusSelectOption[] = useMemo(
    () => [
      {
        label: messages.active,
        value: CollectionStatusEnum.active,
        isDisabled: Boolean(retailerId),
      },
      {
        label: messages.activeAdminOnly,
        value: CollectionStatusEnum.activeAdminOnly,
      },
      {
        label: messages.inactive,
        value: CollectionStatusEnum.inactive,
      },
    ],
    [messages, retailerId]
  )

  useEffect(() => {
    register({ name: 'details.active' })
    register({ name: 'details.activeAdminOnly' })
  }, [register])

  return (
    <CollectionsFormInputWrapper label={messages.label}>
      <SimpleSelect
        value={selectedOption}
        onChange={handleOnChange}
        options={statusOptions}
        placeholder={messages.placeholder}
      />
    </CollectionsFormInputWrapper>
  )
}
